import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip,
  TextField,
  Grid,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';

// 項目名稱含這些關鍵字視為團體項目，可收合為一隊一列
const TEAM_EVENT_RE = /團體|接力|隊/;

// 身障賽事的子組別與分級碼，從項目移到組別（與後端統計同一套規則）
const SUBGROUP_RE = /^(?:腦性麻痺)?(?:站立|輪椅|坐姿)組/;
const CLASS_RE = /((?:[A-Z]{1,4}\d+)(?:[-/][A-Z]{0,4}\d+)*級?(?:計時)?)$/;

// 把完整項目名稱拆成（組別, 項目），與後端統計同一套規則
// 例：'高中部女生組田徑跳高' -> { group: '高中部女生組', event: '跳高' }
//     '肢體障礙女子組田徑輪椅組100公尺T54級'
//       -> { group: '肢體障礙女子組輪椅組T54級', event: '100公尺' }
const parseCompetition = (name, sport) => {
  if (!name) return { group: null, event: null };
  const idx = name.indexOf('組');
  if (idx === -1) return { group: null, event: null };
  let group = name.slice(0, idx + 1);
  let rest = name.slice(idx + 1).trim();
  if (sport && rest.startsWith(sport)) rest = rest.slice(sport.length);
  let event = rest.replace(/×|ｘ/g, 'X').replace(/\s+/g, '');

  const sub = event.match(SUBGROUP_RE);
  if (sub) {
    group += sub[0];
    event = event.slice(sub[0].length);
  }
  const cls = event.match(CLASS_RE);
  if (cls && cls[1] !== event) {
    group += cls[1];
    event = event.slice(0, event.length - cls[1].length);
  }

  return { group, event: event || null };
};

const ResultsTable = ({ results, searchKey, collapseOnSearch = false }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [collapseTeams, setCollapseTeams] = useState(false);
  const [filters, setFilters] = useState({
    sport_chinese: '',
    games_year: '',
    comp_group: '',
    comp_event: '',
    ranking: '',
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.value,
    });
    setPage(0); // 重置到第一頁
  };

  const clearFilters = useCallback(() => {
    setFilters({
      sport_chinese: '',
      games_year: '',
      comp_group: '',
      comp_event: '',
      ranking: '',
    });
    setPage(0);
  }, []);

  // 每次新搜尋時自動清除篩選；從統計分析跳轉的查詢預設團體收合
  useEffect(() => {
    clearFilters();
    setRowsPerPage(10);
    setCollapseTeams(collapseOnSearch);
  }, [searchKey, clearFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // 先為每列解析出組別與項目，篩選與選項共用
  const parsedResults = useMemo(() => {
    if (!results) return [];
    return results.map((row) => {
      const { group, event } = parseCompetition(row.Competition_Name, row.Sport_Chinese);
      return { ...row, _group: group, _event: event };
    });
  }, [results]);

  // 獲取唯一值作為篩選選項（雙向級聯篩選：每個字段根據其他字段的篩選條件動態更新選項）
  const uniqueOptions = useMemo(() => {
    if (parsedResults.length === 0) return {};

    // 根據當前篩選條件過濾結果，排除指定字段的篩選條件
    const getFilteredForField = (excludeField) => {
      return parsedResults.filter(row => {
        if (excludeField !== 'sport_chinese' && filters.sport_chinese && row.Sport_Chinese !== filters.sport_chinese) return false;
        if (excludeField !== 'games_year' && filters.games_year && row.GamesYear?.toString() !== filters.games_year) return false;
        if (excludeField !== 'comp_group' && filters.comp_group && row._group !== filters.comp_group) return false;
        if (excludeField !== 'comp_event' && filters.comp_event && row._event !== filters.comp_event) return false;
        // 注意：名次不參與級聯篩選，避免選擇沒有資料的名次時清空其他選項
        return true;
      });
    };

    // 確保當前選中的值始終出現在選項中
    const getSportsOptions = () => {
      const filtered = getFilteredForField('sport_chinese');
      const options = new Set(filtered.map(r => r.Sport_Chinese).filter(Boolean));
      if (filters.sport_chinese) options.add(filters.sport_chinese);
      return [...options].sort();
    };

    const getYearsOptions = () => {
      const filtered = getFilteredForField('games_year');
      const options = new Set(filtered.map(r => r.GamesYear).filter(Boolean));
      if (filters.games_year) options.add(parseInt(filters.games_year));
      return [...options].sort((a, b) => b - a);
    };

    const getGroupsOptions = () => {
      const filtered = getFilteredForField('comp_group');
      const options = new Set(filtered.map(r => r._group).filter(Boolean));
      if (filters.comp_group) options.add(filters.comp_group);
      return [...options].sort();
    };

    const getEventsOptions = () => {
      const filtered = getFilteredForField('comp_event');
      const options = new Set(filtered.map(r => r._event).filter(Boolean));
      if (filters.comp_event) options.add(filters.comp_event);
      return [...options].sort();
    };

    // 名次選項由實際資料產生，第 1 名排最前；不參與級聯以免互相清空
    const getRankingsOptions = () => {
      const filtered = getFilteredForField('ranking');
      const options = new Set(
        filtered.map(r => parseInt(r.Ranking, 10)).filter(n => !Number.isNaN(n))
      );
      if (filters.ranking) options.add(parseInt(filters.ranking, 10));
      return [...options].sort((a, b) => a - b);
    };

    return {
      sports: getSportsOptions(),
      years: getYearsOptions(),
      groups: getGroupsOptions(),
      events: getEventsOptions(),
      rankings: getRankingsOptions(),
    };
  }, [parsedResults, filters]);

  // 篩選結果
  const filteredResults = useMemo(() => {
    return parsedResults.filter(row => {
      if (filters.sport_chinese && row.Sport_Chinese !== filters.sport_chinese) return false;
      if (filters.games_year && row.GamesYear?.toString() !== filters.games_year) return false;
      if (filters.comp_group && row._group !== filters.comp_group) return false;
      if (filters.comp_event && row._event !== filters.comp_event) return false;
      if (filters.ranking && row.Ranking !== filters.ranking) return false;
      return true;
    });
  }, [parsedResults, filters]);

  // 團體收合：團體、接力項目依賽事＋年份＋項目＋名次＋學校合併成一列，隊員姓名併在同格
  const displayResults = useMemo(() => {
    if (!collapseTeams) return filteredResults;
    const grouped = new Map();
    const output = [];
    filteredResults.forEach((row) => {
      if (!TEAM_EVENT_RE.test(row.Competition_Name || '')) {
        output.push(row);
        return;
      }
      const key = [row.GamesID, row.GamesYear, row.Competition_Name,
        row.Ranking, row.SchoolName].join('|');
      const existing = grouped.get(key);
      if (existing) {
        if (row.Athlete_Name) {
          existing.Athlete_Name = existing.Athlete_Name
            ? `${existing.Athlete_Name}、${row.Athlete_Name}`
            : row.Athlete_Name;
        }
      } else {
        const copy = { ...row };
        grouped.set(key, copy);
        output.push(copy);
      }
    });
    return output;
  }, [filteredResults, collapseTeams]);

  // 結果依名次排序：第 1 名在前，無名次排最後，同名次者年份新的在前
  const sortedResults = useMemo(() => {
    const rankOf = (row) => {
      const n = parseInt(row.Ranking, 10);
      return Number.isNaN(n) ? Infinity : n;
    };
    return [...displayResults].sort((a, b) => {
      const diff = rankOf(a) - rankOf(b);
      if (diff !== 0) return diff;
      return (b.GamesYear || 0) - (a.GamesYear || 0);
    });
  }, [displayResults]);

  const getRankingColor = (ranking) => {
    if (!ranking) return 'default';
    const rank = parseInt(ranking);
    if (rank === 1) return 'error';
    if (rank === 2) return 'warning';
    if (rank === 3) return 'success';
    return 'default';
  };

  if (!results || results.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          暫無查詢結果
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          請使用上方搜尋表單進行查詢
        </Typography>
      </Paper>
    );
  }

  const paginatedResults = sortedResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <Paper elevation={3}>
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6">
          查詢結果
          <Chip
            label={`共 ${results.length} 筆`}
            color="primary"
            size="small"
            sx={{ ml: 2 }}
          />
          {filteredResults.length < results.length && (
            <Chip
              label={`篩選後 ${filteredResults.length} 筆`}
              color="secondary"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {collapseTeams && displayResults.length < filteredResults.length && (
            <Chip
              label={`已收合 ${filteredResults.length - displayResults.length} 筆隊員資料`}
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
          <ToggleButtonGroup
            value={collapseTeams ? 'team' : 'person'}
            exclusive
            size="small"
            onChange={(e, v) => {
              if (v === null) return;
              setCollapseTeams(v === 'team');
              setPage(0);
            }}
          >
            {[
              ['person', PersonIcon, '個人檢視'],
              ['team', GroupsIcon, '團體收合'],
            ].map(([value, Icon, label]) => (
              <ToggleButton
                key={value}
                value={value}
                sx={{
                  px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                <Icon fontSize="small" sx={{ mr: 0.5 }} />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* 篩選區域 */}
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: 'grey.50' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1">
              表格篩選
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} 個篩選`}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="運動類別"
                value={filters.sport_chinese}
                onChange={handleFilterChange('sport_chinese')}
                size="small"
              >
                <MenuItem value="">全部</MenuItem>
                {uniqueOptions.sports?.map((sport) => (
                  <MenuItem key={sport} value={sport}>
                    {sport}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="賽事年份"
                value={filters.games_year}
                onChange={handleFilterChange('games_year')}
                size="small"
              >
                <MenuItem value="">全部</MenuItem>
                {uniqueOptions.years?.map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="組別"
                value={filters.comp_group}
                onChange={handleFilterChange('comp_group')}
                size="small"
              >
                <MenuItem value="">全部</MenuItem>
                {uniqueOptions.groups?.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="項目"
                value={filters.comp_event}
                onChange={handleFilterChange('comp_event')}
                size="small"
              >
                <MenuItem value="">全部</MenuItem>
                {uniqueOptions.events?.map((ev) => (
                  <MenuItem key={ev} value={ev}>
                    {ev}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                label="名次"
                value={filters.ranking}
                onChange={handleFilterChange('ranking')}
                size="small"
              >
                <MenuItem value="">全部</MenuItem>
                {uniqueOptions.rankings?.map((rank) => (
                  <MenuItem key={rank} value={rank.toString()}>
                    第{rank}名
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                fullWidth
                size="small"
              >
                清除
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>運動類別</TableCell>
              <TableCell>運動員姓名</TableCell>
              <TableCell>賽事名稱</TableCell>
              <TableCell>賽事年份</TableCell>
              <TableCell>賽事等級</TableCell>
              <TableCell>賽事地點</TableCell>
              <TableCell>競賽項目</TableCell>
              <TableCell>名次</TableCell>
              <TableCell>成績</TableCell>
              <TableCell>單位</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedResults.map((row, index) => (
              <TableRow
                key={`${row.GUID}-${row.GamesID}-${index}`}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>{row.Sport_Chinese || '-'}</TableCell>
                <TableCell>
                  <strong>{row.Athlete_Name || '-'}</strong>
                </TableCell>
                <TableCell>{row.GamesName || '-'}</TableCell>
                <TableCell>{row.GamesYear || '-'}</TableCell>
                <TableCell>{row.GamesLevel || '-'}</TableCell>
                <TableCell>{row.GamesLocation || '-'}</TableCell>
                <TableCell>{row.Competition_Name || '-'}</TableCell>
                <TableCell>
                  {row.Ranking ? (
                    <Chip
                      icon={<EmojiEventsIcon />}
                      label={`第${row.Ranking}名`}
                      color={getRankingColor(row.Ranking)}
                      size="small"
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{row.Score || '-'}</TableCell>
                <TableCell>{row.SchoolName || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={displayResults.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="每頁顯示："
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} 共 ${count} 筆`
        }
      />
    </Paper>
  );
};

export default ResultsTable;
